import { customElement, scss } from '../../core/ids-decorators';
import IdsElement from '../../core/ids-element';
import { requestAnimationTimeout } from '../../utils/ids-timer-utils/ids-timer-utils';

import IdsEventsMixin from '../../mixins/ids-events-mixin/ids-events-mixin';
import IdsModuleNavDisplayModeMixin from './ids-module-nav-display-mode-mixin';
import IdsModuleNavTextDisplayMixin from './ids-module-nav-text-display-mixin';

import styles from './ids-module-nav-switcher.scss';

import type IdsModuleNavButton from './ids-module-nav-button';
import type IdsDropdown from '../ids-dropdown/ids-dropdown';
import type IdsListBox from '../ids-list-box/ids-list-box';
import type IdsListBoxOption from '../ids-list-box/ids-list-box-option';
import type IdsIcon from '../ids-icon/ids-icon';

const Base = IdsModuleNavTextDisplayMixin(
  IdsModuleNavDisplayModeMixin(
    IdsEventsMixin(
      IdsElement
    )
  )
);

/**
 * IDS Module Nav Switcher Component
 * @type {IdsModuleNavSwitcher}
 * @inherits IdsElement
 * @mixes IdsEventsMixin
 * @mixes IdsModuleNavDisplayModeMixin
 * @mixes IdsModuleNavTextDisplayMixin
 */
@customElement('ids-module-nav-switcher')
@scss(styles)
export default class IdsModuleNavSwitcher extends Base {
  constructor() {
    super();
  }

  connectedCallback() {
    super.connectedCallback();
    this.#attachEventHandlers();

    // Delay setup of Popup-related configuration in order to affect style
    // @TODO make this async/await when possible
    requestAnimationTimeout(() => {
      this.configureComponents();
    }, 2);
  }

  disconnectedCallback() {
    super.disconnectedCallback();
  }

  /**
   * Return the attributes we handle as getters/setters
   * @returns {Array} The attributes in an array
   */
  static get attributes(): Array<any> {
    return [...super.attributes];
  }

  /**
   * @returns {string} Module Switcher component template
   */
  template() {
    return `<div class="ids-module-nav-switcher">
      <slot></slot>
    </div>`;
  }

  /**
   * @readonly
   * @returns {IdsModuleNavButton} slotted Module Button element
   */
  get moduleButtonEl() {
    return this.querySelector<IdsModuleNavButton>('ids-module-nav-button');
  }

  /**
   * @readonly
   * @returns {IdsIcon} slotted Module Button Icon element
   */
  get moduleButtonIconEl() {
    return this.moduleButtonEl?.querySelector<IdsIcon>('ids-icon');
  }

  /**
   * @readonly
   * @returns {IdsDropdown} slotted Module Button element
   */
  get roleDropdownEl() {
    return this.querySelector<IdsDropdown>('ids-dropdown');
  }

  get roleDropdownListEl() {
    return this.roleDropdownEl?.dropdownList;
  }

  /**
   * Fix icon dimensions for all list box options
   * @private
   */
  #fixIconDimensions() {
    const dropdown = this.roleDropdownEl;
    if (!dropdown) return;

    const listBox = dropdown.querySelector<IdsListBox>('ids-list-box');
    if (!listBox) return;

    const options = listBox.children;
    for (let i = 0; i < options.length; i++) {
      const opt = options[i] as IdsListBoxOption;
      const icon = opt.querySelector<IdsIcon>('ids-icon');
      if (icon) {
        icon.setAttribute('height', '32');
        icon.setAttribute('width', '32');
        icon.setAttribute('viewbox', '0 0 32 32');
        icon.setAttribute('stroke', 'none');
      }
    }
  }

  #applyModuleNavClasses() {
    if (!this.roleDropdownEl) return;

    const listBox = this.roleDropdownEl.querySelector<IdsListBox>('ids-list-box');
    if (listBox) {
      listBox.classList.add('module-nav');
      this.handleModuleNavSelection(listBox);
    }

    const opts = [...this.roleDropdownEl.querySelectorAll<IdsListBoxOption>('ids-list-box-option')];
    opts.forEach((opt) => {
      opt.classList.add('module-nav');

      opt.style.borderLeft = 'none';
    });
  }

  configureComponents() {
    const COLOR_VARIANT_NAME = 'module-nav';
    const POPUP_X_OFFSET = 6;
    const POPUP_Y_OFFSET = 22;

    if (this.moduleButtonEl) {
      this.moduleButtonEl.colorVariant = COLOR_VARIANT_NAME;
      this.moduleButtonEl.displayMode = this.displayMode;
    }

    if (this.roleDropdownEl) {
      this.roleDropdownEl.colorVariant = COLOR_VARIANT_NAME;
      this.roleDropdownEl.dropdownIcon = 'expand-all';
      this.roleDropdownEl.label = 'Role Selector';
      this.roleDropdownEl.labelState = 'collapsed';
      this.roleDropdownEl.showListItemIcon = false;
      this.roleDropdownEl.size = 'full';

      this.roleDropdownEl.onColorVariantRefresh = () => {
        const dropdownEl = this.roleDropdownEl;
        if (!dropdownEl) return;

        // Always hide the icon for Module Switcher Dropdown trigger elements
        if (dropdownEl) {
          dropdownEl.showListItemIcon = false;
        }

        if (dropdownEl.input) dropdownEl.input.colorVariant = COLOR_VARIANT_NAME;
        if (dropdownEl.dropdownList) {
          dropdownEl.dropdownList.colorVariant = COLOR_VARIANT_NAME;
          if (dropdownEl.dropdownList.popup) {
            dropdownEl.dropdownList.popup.type = COLOR_VARIANT_NAME;
          }
        }
      };

      const listBox = this.roleDropdownEl.querySelector<IdsListBox>('ids-list-box');
      listBox?.classList.add('module-nav');

      const opts = [...this.roleDropdownEl.querySelectorAll<IdsListBoxOption>('ids-list-box-option')];
      opts.forEach((opt) => opt.classList.add('module-nav'));
    }

    if (this.roleDropdownListEl) {
      this.roleDropdownListEl.showListItemIcon = true;

      // Always reposition this popup correctly
      const popup = this.roleDropdownListEl?.popup;
      if (popup) {
        popup.type = 'module-nav';
        popup.align = 'bottom, left';
        popup.arrow = 'none';

        popup.onPlaceEnd = (popupRect: DOMRect, elem: HTMLElement) => {
          elem.style.insetInlineStart = `${POPUP_X_OFFSET}px`;
          elem.style.top = `${(parseInt(String(this.roleDropdownListEl?.offsetTop || 0)) + POPUP_Y_OFFSET)}px`;
          return popupRect;
        };
      }
    }
  }

  #attachEventHandlers() {
    const dropdown = this.roleDropdownEl;
    if (!dropdown) return;

    // Whenever IdsDropdown opens, reconfigure some styles
    this.onEvent('open', this.roleDropdownEl, () => {
      this.configureComponents();
      this.#fixIconDimensions();
      this.#applyModuleNavClasses();
    });

    this.onEvent('filtered', dropdown, () => {
      this.#fixIconDimensions();
      this.#applyModuleNavClasses();

      const listBox = dropdown.querySelector<IdsListBox>('ids-list-box');
      if (listBox) {
        const option = listBox.querySelector('ids-list-box-option') as HTMLElement;
        if (option) {
          option.style.borderLeft = 'none';
        }
      }
    });

    this.onEvent('change', dropdown, () => {
      requestAnimationTimeout(() => {
        this.#fixIconDimensions();
        this.#applyModuleNavClasses();
      }, 0);
    });

    this.onEvent('selected', this.roleDropdownEl, (e: CustomEvent) => {
      const selectedOption = (e.detail.selectedElem as IdsListBoxOption | null);
      const icon = selectedOption?.querySelector<IdsIcon>('ids-icon');
      const moduleButtonIconEl = this.moduleButtonIconEl;
      if (moduleButtonIconEl && icon) moduleButtonIconEl.icon = icon.icon;
    });
  }

  onDisplayModeChange(): void {
    this.configureComponents();
  }

  /**
   * Handles the selection state for module nav list box options
   * @param {HTMLElement} listBox - The list box element
   */
  handleModuleNavSelection(listBox: HTMLElement) {
    if (!listBox) return;

    const options = [...listBox.querySelectorAll('ids-list-box-option')];
    let originalSelected: HTMLElement | null = null;

    options.forEach((opt) => {
      if (opt.classList.contains('is-selected')) {
        originalSelected = opt as HTMLElement;
      }
    });

    options.forEach((opt) => {
      opt.addEventListener('mouseover', () => {
        options.forEach((o) => {
          if (o.classList.contains('is-selected')) {
            o.classList.remove('is-selected');
          }
        });
        opt.classList.add('hover-active');
      });

      opt.addEventListener('mouseout', () => {
        opt.classList.remove('hover-active');
      });
    });

    const dropdown = listBox.closest('ids-dropdown');
    if (dropdown) {
      dropdown.addEventListener('close', () => {
        const hasSelected = options.some((opt) => opt.classList.contains('is-selected'));
        if (!hasSelected && originalSelected) {
          options.forEach((opt) => opt.classList.remove('is-selected'));
          originalSelected.classList.add('is-selected');
        }
      });
    }
  }
}
